import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../ui/Button';
import { UrgencyBanner } from '../ui/UrgencyBanner';
import { Shield, CreditCard, CheckCircle2, Check } from 'lucide-react';
interface PricingSectionProps {
  id?: string;
  isFinal?: boolean;
}
type PlanId = 'standard' | 'elite';
const PLANS: Record<PlanId, {
  name: string;
  subtitle: string;
  oldPrice: string;
  price: string;
  features: string[];
  url: string;
}> = {
  standard: {
    name: 'KIT PROFESIONAL STANDARD',
    subtitle: '6 Tomos + 3 Bonos Estratégicos',
    oldPrice: '$34.990',
    price: '$19.990',
    features: ['6 Tomos Completos', '3 Bonos Estratégicos', 'Acceso Inmediato'],
    url: 'https://checkout.solucionesya.shop/cart/48561847337208:1'
  },
  elite: {
    name: 'KIT PROFESIONAL PRO ÉLITE',
    subtitle: '6 Tomos + 3 Bonos + Módulos Pro Élite™',
    oldPrice: '$43.990',
    price: '$27.990',
    features: ['Todo lo del Kit Standard', 'M1: Sistema de Programación y Costos', 'M2: Negocio del Entrenamiento'],
    url: 'https://checkout.solucionesya.shop/cart/48641211433208:1'
  }
};
export function PricingSection({
  id,
  isFinal = false
}: PricingSectionProps) {
  // Pro Élite preselected
  const [selected, setSelected] = useState<PlanId>('elite');
  const activePlan = PLANS[selected];
  const handleBuy = () => {
    window.open(activePlan.url, '_blank', 'noopener,noreferrer');
  };
  return <section id={id} className={`py-10 sm:py-16 lg:py-24 ${isFinal ? 'bg-brand-dark text-white' : 'bg-brand-bg'}`} data-id="element-140">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-141">
        {/* Urgency banner — sits above both pricing modules */}
        <UrgencyBanner data-id="element-142" />

        {/* Header */}
        <div className="text-center mb-5 sm:mb-8" data-id="element-143">
          {isFinal && <h2 className="text-xl sm:text-3xl md:text-5xl font-extrabold text-white mb-3 sm:mb-5" data-id="element-144">
              No dejes pasar esta oportunidad de ordenar tu negocio.
            </h2>}
          <h3 className={`text-base sm:text-2xl font-bold mb-1 ${isFinal ? 'text-brand-accent' : 'text-[#3560AC]'}`} data-id="element-145">
            Elegí tu Kit de Presupuestos Rápidos
          </h3>
          <p className={`text-xs sm:text-base ${isFinal ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-146">
            Tocá una opción para seleccionarla — ambas incluyen los 3 bonos
          </p>
        </div>

        {/* Selectable Pricing Cards — horizontal rows (stacked vertically) */}
        <div className="flex flex-col gap-3 sm:gap-4 mb-5 sm:mb-8" data-id="element-147">
          {(['standard', 'elite'] as PlanId[]).map(planId => {
          const plan = PLANS[planId];
          const isSelected = selected === planId;
          const isElite = planId === 'elite';
          return <motion.button key={planId} type="button" onClick={() => setSelected(planId)} whileTap={{
            scale: 0.99
          }} whileHover={{
            x: 2
          }} aria-pressed={isSelected} className={`relative w-full text-left rounded-xl sm:rounded-2xl overflow-hidden transition-all duration-200 ${isSelected ? isElite ? 'ring-2 sm:ring-4 ring-brand-accent bg-white shadow-2xl shadow-brand-accent/30' : 'ring-2 sm:ring-4 ring-brand-dark bg-white shadow-xl' : isFinal ? 'bg-white/5 ring-1 ring-white/20 opacity-70 hover:opacity-100' : 'bg-white ring-1 ring-gray-200 opacity-80 hover:opacity-100'}`} data-id="element-148">
                {/* Elite ribbon — top-left corner badge */}
                {isElite && <div className="absolute top-0 left-0 bg-brand-accent text-brand-dark font-black px-2 py-0.5 sm:px-3 sm:py-1 text-[9px] sm:text-xs uppercase tracking-wider rounded-br-lg" data-id="element-149">
                    ⭐ Más Elegido
                  </div>}

                <div className={`flex items-center gap-3 sm:gap-5 p-3 sm:p-5 ${isElite ? 'pt-6 sm:pt-7' : ''}`} data-id="element-150">
                  {/* Radio indicator */}
                  <div className={`w-6 h-6 sm:w-7 sm:h-7 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${isSelected ? isElite ? 'border-brand-accent bg-brand-accent' : 'border-brand-dark bg-brand-dark' : 'border-gray-300 bg-white'}`} data-id="element-151">
                    {isSelected && <Check className="w-4 h-4 sm:w-5 sm:h-5 text-white" strokeWidth={3} data-id="element-152" />}
                  </div>

                  {/* Name + subtitle */}
                  <div className="flex-1 min-w-0" data-id="element-153">
                    <div className="flex items-center gap-2 flex-wrap" data-id="element-154">
                      <h4 className="font-black text-brand-dark leading-tight text-sm sm:text-lg md:text-xl" data-id="element-155">
                        {plan.name}
                      </h4>
                      {isElite && <span className="bg-green-100 text-green-700 text-[9px] sm:text-xs font-bold px-1.5 py-0.5 rounded whitespace-nowrap" data-id="element-156">
                          AHORRÁS $16K
                        </span>}
                    </div>
                    <p className="text-[10px] sm:text-xs md:text-sm text-gray-600 font-medium leading-snug mt-0.5" data-id="element-157">
                      {plan.subtitle}
                    </p>
                  </div>

                  {/* Price */}
                  <div className="flex-shrink-0 text-right" data-id="element-158">
                    <div className="text-[10px] sm:text-sm text-gray-400 line-through leading-none" data-id="element-159">
                      {plan.oldPrice}
                    </div>
                    <div className="font-black text-brand-dark text-xl sm:text-3xl md:text-4xl leading-tight" data-id="element-160">
                      {plan.price}
                    </div>
                  </div>
                </div>
              </motion.button>;
        })}
        </div>

        {/* Selected plan features panel */}
        <div className="mb-5 sm:mb-6 bg-white/80 backdrop-blur rounded-lg sm:rounded-xl p-3 sm:p-4 border border-gray-200" data-id="element-161">
          <p className="text-[10px] sm:text-xs uppercase tracking-wider font-bold text-gray-500 mb-2" data-id="element-162">
            Incluye:
          </p>
          <ul className="grid grid-cols-1 sm:grid-cols-3 gap-1.5 sm:gap-3" data-id="element-163">
            {activePlan.features.map((f, i) => <li key={i} className="flex items-start text-xs sm:text-sm text-gray-700" data-id="element-164">
                <CheckCircle2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 mt-0.5 flex-shrink-0 text-brand-accent" data-id="element-165" />
                <span data-id="element-166">{f}</span>
              </li>)}
          </ul>
        </div>

        {/* Single shared CTA */}
        <motion.div key={selected} initial={{
        opacity: 0.6,
        y: 5
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.25
      }} className="text-center" data-id="element-167">
          <Button size="xl" onClick={handleBuy} className="w-full sm:w-auto sm:min-w-[400px] shadow-2xl shadow-brand-accent/40 text-sm sm:text-xl py-4 sm:py-5" data-id="element-168">
            👉 QUIERO MI KIT PROFESIONAL
          </Button>
          <p className={`text-[11px] sm:text-sm mt-3 font-medium ${isFinal ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-169">
            Estás comprando:{' '}
            <span className={`font-bold ${isFinal ? 'text-brand-accent' : 'text-brand-dark'}`} data-id="element-170">
              {activePlan.name}
            </span>
          </p>
        </motion.div>

        {/* Trust strip */}
        <div className={`text-center mt-5 sm:mt-8 flex flex-wrap justify-center items-center gap-x-3 sm:gap-x-6 gap-y-1.5 sm:gap-y-3 text-[10px] sm:text-sm font-medium ${isFinal ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-171">
          <span className="flex items-center" data-id="element-172">
            <Shield className="w-3 h-3 sm:w-4 sm:h-4 mr-1 text-green-500" data-id="element-173" />{' '}
            Garantía 7 días
          </span>
          <span className="flex items-center" data-id="element-174">
            <CreditCard className="w-3 h-3 sm:w-4 sm:h-4 mr-1 text-blue-500" data-id="element-175" />{' '}
            Pago seguro
          </span>
          <span className="flex items-center" data-id="element-176">
            <span className="font-bold text-blue-400 mr-1" data-id="element-177">mp</span> Mercado
            Pago
          </span>
          <span className="flex items-center" data-id="element-178">⚡ Acceso en 5 min</span>
        </div>
      </div>
    </section>;
}